import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from '../search';

export interface TabSortSpec {
  by: string;
  dir: 'asc' | 'desc';
}

/** sessionStorage key for a table's per-tab sort, scoped by campaign. One format,
 *  one place — `scope` names the table (e.g. 'creators', 'confirmed'). */
export const rosterSortStorageKey = (scope: string, campaignId: string | undefined): string =>
  `discovr:${scope}-sort:${campaignId ?? 'none'}`;

/** The `sort` prop shape DataTable expects (by:null = unsorted). */
export interface DataTableSort {
  by: string | null;
  dir: 'asc' | 'desc';
  onChange: (next: { by: string | null; dir: 'asc' | 'desc' }) => void;
}

/** The `?sort_by=&sort_dir=` params a list query sends, or `{}` when a tab is
 *  unsorted — spread straight into the query key and the API call so every tab
 *  threads the sort contract identically (single source, no drift). */
export interface TabSortParams {
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
}

export interface RosterTabSort<K extends string> {
  /** Full per-tab state — add to a query's dep array so a sort change refetches. */
  state: Partial<Record<K, TabSortSpec>>;
  /** The persisted {by,dir} for one tab, or undefined. Read inside buildPageQuery. */
  specFor: (tab: K) => TabSortSpec | undefined;
  /** The query params for a tab ({} when unsorted). Spread into the query key
   *  AND the list call so both stay in lockstep — the one place the param shape
   *  lives, shared by every roster tab. */
  sortParamsFor: (tab: K) => TabSortParams;
  /** Builds the DataTable `sort` prop for a tab. `disabled` (e.g. public view)
   *  returns undefined; `onChange` runs after a change (e.g. reset the page). */
  sortProp: (tab: K, opts?: { disabled?: boolean; onChange?: () => void }) => DataTableSort | undefined;
}

/** Read a storage key's persisted per-tab sort, tolerating absent/corrupt JSON
 *  and an unavailable store (private mode / SSR) by falling back to empty. */
function readSortState<K extends string>(storageKey: string): Partial<Record<K, TabSortSpec>> {
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as Partial<Record<K, TabSortSpec>>) : {};
  } catch {
    return {};
  }
}

/**
 * Per-tab column sort ({by,dir} keyed by sub-tab), persisted to sessionStorage
 * for the visit so it survives pill-switching + navigation — AND mirrored in
 * the URL (?sort_by=&sort_dir=) for whichever pill (`activeTab`) is currently
 * on screen, so a copied link reproduces the exact sorted view (matching how
 * ?page=/?pill= already work). The URL only ever reflects the ACTIVE pill —
 * switching pills never carries one pill's sort into another; each pill keeps
 * its own memory, same as before.
 *
 * The URL seeds the active pill's sort ONCE, in the initial-state lazy
 * initializer (so a shared link opens straight into the sorted view) — and
 * ONLY when `?pill=` matches `activeTab`, i.e. the pill this component is
 * mounting into is the SAME one the URL's sort was written for. Without that
 * guard, `sort_by`/`sort_dir` (single shared fields, like `page`/`pill`) would
 * leak a leftover value across TOP-LEVEL tabs too: e.g. sorting Creators by
 * followers, then switching to Confirmed (a fresh mount, its own storageKey)
 * would otherwise inherit Creators' stale `sort_by` from the URL before
 * Confirmed's own mirror effect has a chance to overwrite it. `activeTab` in
 * every real caller is itself derived from `?pill=` (with a default fallback),
 * so a genuine shared link naturally satisfies this — only a stale leftover
 * from a DIFFERENT tab/pill fails it.
 *
 * After mount, sessionStorage state is the sole source of truth and the URL
 * only ever follows it (state → URL, one-directional) — a pill switch
 * re-mirrors whatever that pill already had stored, it never re-reads the
 * URL, so a stale URL value can't leak into the wrong pill's memory that way
 * either.
 */
export function useRosterTabSort<K extends string>(storageKey: string, activeTab: K): RosterTabSort<K> {
  const navigate = useNavigate();
  const urlSearch = useSearch({ strict: false }) as Partial<CampaignSearch>;

  const [state, setState] = useState<Partial<Record<K, TabSortSpec>>>(() => {
    const stored = readSortState<K>(storageKey);
    if (!urlSearch.sort_by || urlSearch.pill !== activeTab) return stored;
    return { ...stored, [activeTab]: { by: urlSearch.sort_by, dir: urlSearch.sort_dir ?? 'desc' } };
  });

  // Re-seed from the NEW key when storageKey changes. The tab components are not
  // remounted when you navigate between campaigns, so without this the state
  // carried over from the previous campaign, and the persist effect below would
  // then write THAT into the new campaign's key — clobbering its saved sort.
  // Adjusting state during render (React's reset-on-prop-change pattern) makes the
  // persist effect see the new key's own value, so no stale write ever happens.
  const [previousStorageKey, setPreviousStorageKey] = useState(storageKey);
  if (previousStorageKey !== storageKey) {
    setPreviousStorageKey(storageKey);
    setState(readSortState<K>(storageKey));
  }

  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(state));
    } catch {
      /* sessionStorage unavailable (private mode / SSR) — sort just won't persist */
    }
  }, [state, storageKey]);

  // Mirror the ACTIVE pill's spec into the URL — fires on a user sort change
  // AND on switching to a pill with a different stored spec, so the URL always
  // reflects whatever's on screen. `replace` (not push): this is a read-along
  // mirror, not a navigational step Back should undo — Back/Forward across
  // pill switches is already handled by ?pill= itself.
  const activeSpec = state[activeTab];
  useEffect(() => {
    void navigate({
      to: '.',
      replace: true,
      search: ((p: Partial<CampaignSearch>) => ({
        ...p,
        sort_by: activeSpec?.by,
        sort_dir: activeSpec?.dir,
      })) as never,
      resetScroll: false,
    } as never);
  }, [activeTab, activeSpec?.by, activeSpec?.dir, navigate]);

  const specFor = useCallback((tab: K) => state[tab], [state]);

  const sortParamsFor = useCallback<RosterTabSort<K>['sortParamsFor']>(
    (tab) => {
      const active = state[tab];
      return active ? { sort_by: active.by, sort_dir: active.dir } : {};
    },
    [state],
  );

  const sortProp = useCallback<RosterTabSort<K>['sortProp']>(
    (tab, opts) => {
      if (opts?.disabled) return undefined;
      const active = state[tab];
      return {
        by: active?.by ?? null,
        dir: active?.dir ?? 'desc',
        onChange: ({ by, dir }) => {
          setState((prev) => ({ ...prev, [tab]: by ? { by, dir } : undefined }));
          opts?.onChange?.();
        },
      };
    },
    [state],
  );

  // Stable reference so consumers can depend on `rosterSort` in useCallback/effect
  // dep arrays without recreating them every render (state/specFor/sortProp only
  // change when the sort actually changes).
  return useMemo(
    () => ({ state, specFor, sortParamsFor, sortProp }),
    [state, specFor, sortParamsFor, sortProp],
  );
}
